import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    ArrowLeft, Save, Download, Plus, Trash2, ExternalLink, CheckCircle2, ArrowRight,
} from "lucide-react";
import { getTurnover, updateTurnover, advanceTurnover } from "@/lib/api";
import { germanDate, euro } from "@/lib/format";
import PhaseTimeline from "@/components/PhaseTimeline";
import StatusBadge from "@/components/StatusBadge";
import SignaturePad from "@/components/SignaturePad";
import SuchAuswahl from "@/components/ui/such-auswahl";
import {
    exportKuendigungsbestaetigung, exportUebergabeprotokoll,
    exportWohnungsgeberbestaetigung, exportKautionsabrechnung,
} from "@/lib/pdf";
import { toast } from "sonner";
import { useSchreibschutz } from "@/lib/auth";
import { sperre } from "@/hilfe/Knopfhilfe";

const SectionCard = ({ title, subtitle, children, right }) => (
    <div className="bg-white border border-gray-200 rounded-md">
        <div className="flex items-start justify-between gap-3 px-5 md:px-6 py-4 border-b border-gray-200">
            <div>
                <div className="font-heading font-semibold text-slate-900">{title}</div>
                {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
            </div>
            {right}
        </div>
        <div className="p-5 md:p-6">{children}</div>
    </div>
);

const Field = ({ label, children, className = "" }) => (
    <div className={className}>
        <Label className="text-xs text-gray-600 mb-1.5 inline-block">{label}</Label>
        {children}
    </div>
);

// ---------------- PHASE 1: Kündigung ----------------
const PhaseKuendigung = ({ detail, t, setT, save }) => {
    const { gesperrt, grund } = useSchreibschutz();
    return (
    <SectionCard
        title="Kündigungs-Workflow [M.1.1]"
        subtitle="Legally konforme Erfassung, automatische Fristberechnung."
        right={
            <Button
                variant="outline" size="sm"
                onClick={() => exportKuendigungsbestaetigung({
                    turnover: t, unit: detail.unit, property: detail.property, entity: detail.entity, oldTenant: detail.old_tenant,
                })}
                data-testid="btn-export-kuendigung"
            >
                <Download size={14} className="mr-1.5" /> Kündigungsbestätigung PDF
            </Button>
        }
    >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Eingang der Kündigung">
                <Input type="date" value={t.termination?.kuendigung_eingang || ""} onChange={(e) => setT({ ...t, termination: { ...t.termination, kuendigung_eingang: e.target.value } })} data-testid="ku-eingang" />
            </Field>
            <Field label="Kündigungsdatum des Mieters">
                <Input type="date" value={t.termination?.kuendigung_datum_mieter || ""} onChange={(e) => setT({ ...t, termination: { ...t.termination, kuendigung_datum_mieter: e.target.value } })} data-testid="ku-datum" />
            </Field>
            <Field label="Kündigungsfrist (Monate)">
                <Input type="number" min={1} max={12} value={t.termination?.kuendigungsfrist_monate ?? 3} onChange={(e) => setT({ ...t, termination: { ...t.termination, kuendigungsfrist_monate: parseInt(e.target.value || "3") } })} data-testid="ku-frist" />
            </Field>
            <Field label="Auszugsdatum (berechnet)">
                <Input type="date" value={t.termination?.auszugsdatum_berechnet || ""} onChange={(e) => setT({ ...t, termination: { ...t.termination, auszugsdatum_berechnet: e.target.value } })} data-testid="ku-auszug" />
                <div className="text-[11px] text-gray-500 mt-1">Wird beim Speichern automatisch anhand Eingang + Frist berechnet (zum Monatsende).</div>
            </Field>
            <Field label="Strategie" className="md:col-span-2">
                <SuchAuswahl wert={t.termination?.strategie || ""} onWaehlen={(v) => setT({ ...t, termination: { ...t.termination, strategie: v } })}
                    optionen={[
                        { wert: "neuvermietung", text: "Neuvermietung" },
                        { wert: "verkauf", text: "Verkauf" },
                        { wert: "eigenbedarf", text: "Eigenbedarf" },
                    ]}
                    platzhalter="Auswählen"
                    testId="ku-strategie"
                />
            </Field>
            <Field label="Neue Anschrift Mieter (für Kautionsabrechnung)" className="md:col-span-2">
                <Input value={t.termination?.neue_adresse_mieter || ""} onChange={(e) => setT({ ...t, termination: { ...t.termination, neue_adresse_mieter: e.target.value } })} data-testid="ku-neue-adresse" />
            </Field>
            <div className="md:col-span-2 flex items-center gap-2">
                <Checkbox id="bestaetigung" checked={!!t.termination?.bestaetigung_versendet} onCheckedChange={(v) => setT({ ...t, termination: { ...t.termination, bestaetigung_versendet: Boolean(v) } })} data-testid="ku-bestaetigung" />
                <label htmlFor="bestaetigung" className="text-sm text-slate-700">Kündigungsbestätigung versendet</label>
            </div>
        </div>
        <div className="flex justify-end pt-5">
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-phase1"
                    disabled={gesperrt} grund={grund}>
                <Save size={14} className="mr-1.5" /> Speichern
            </Button>
        </div>
    </SectionCard>
    );
};

// ---------------- PHASE 2: Vorabnahme ----------------
const PhaseVorabnahme = ({ detail, t, setT, save }) => {
    const { gesperrt, grund } = useSchreibschutz();
    const addMangel = () => setT({
        ...t,
        pre_inspection: {
            ...t.pre_inspection,
            maengel: [...(t.pre_inspection?.maengel || []), { raum: "", beschreibung: "", kategorie: "abnutzung", erledigt: false }],
        },
    });
    const updateMangel = (i, patch) => {
        const arr = [...(t.pre_inspection?.maengel || [])];
        arr[i] = { ...arr[i], ...patch };
        setT({ ...t, pre_inspection: { ...t.pre_inspection, maengel: arr } });
    };
    const removeMangel = (i) => {
        const arr = [...(t.pre_inspection?.maengel || [])];
        arr.splice(i, 1);
        setT({ ...t, pre_inspection: { ...t.pre_inspection, maengel: arr } });
    };

    return (
        <SectionCard
            title="Vorabnahme & Foto-Referenz [M.1.2]"
            subtitle="Zustand gegen Bestandsfotos prüfen. To-Do-Liste für den Mieter erstellen."
            right={
                detail.property?.bestandsfotos_link ? (
                    <a href={detail.property.bestandsfotos_link} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm"><ExternalLink size={14} className="mr-1.5" />Bestandsfotos (Drive)</Button>
                    </a>
                ) : null
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <Field label="Datum Vorabnahme">
                    <Input type="date" value={t.pre_inspection?.datum || ""} onChange={(e) => setT({ ...t, pre_inspection: { ...t.pre_inspection, datum: e.target.value } })} data-testid="vor-datum" />
                </Field>
                <Field label="Durchgeführt von">
                    <Input value={t.pre_inspection?.durchgefuehrt_von || ""} onChange={(e) => setT({ ...t, pre_inspection: { ...t.pre_inspection, durchgefuehrt_von: e.target.value } })} />
                </Field>
                <Field label="Referenz-Fotos (Drive-Link)">
                    <Input value={t.pre_inspection?.referenz_fotos_link || ""} onChange={(e) => setT({ ...t, pre_inspection: { ...t.pre_inspection, referenz_fotos_link: e.target.value } })} placeholder="https://drive.google.com/..." />
                </Field>
            </div>

            <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-slate-900">Mängel & Schönheitsreparaturen</div>
                <Button type="button" variant="outline" size="sm" onClick={addMangel} data-testid="btn-add-mangel"
                        disabled={gesperrt} grund={grund}>
                    <Plus size={14} className="mr-1" /> Position
                </Button>
            </div>
            <div className="space-y-2">
                {(t.pre_inspection?.maengel || []).map((m, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start bg-gray-50 border border-gray-200 rounded-md p-3">
                        <Input className="col-span-2" placeholder="Raum" value={m.raum || ""} onChange={(e) => updateMangel(i, { raum: e.target.value })} />
                        <Input className="col-span-5" placeholder="Beschreibung" value={m.beschreibung || ""} onChange={(e) => updateMangel(i, { beschreibung: e.target.value })} />
                        <SuchAuswahl wert={m.kategorie || "abnutzung"} onWaehlen={(v) => updateMangel(i, { kategorie: v })}
                            optionen={[
                                { wert: "abnutzung", text: "Abnutzung" },
                                { wert: "schaden", text: "Schaden" },
                                { wert: "schoenheitsreparatur", text: "Schönheitsrep." },
                            ]}
                            className="col-span-2"
                        />
                        <Input className="col-span-2" type="number" step="0.01" placeholder="Kosten" value={m.geschätzte_kosten ?? ""} onChange={(e) => updateMangel(i, { geschätzte_kosten: e.target.value ? parseFloat(e.target.value) : null })} />
                        <Button variant="ghost" size="sm" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeMangel(i)} data-testid={`btn-remove-mangel-${i}`}
                                disabled={gesperrt} grund={grund}>
                            <Trash2 size={14} />
                        </Button>
                    </div>
                ))}
                {(t.pre_inspection?.maengel || []).length === 0 && (
                    <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md py-6 text-center">Keine Mängel erfasst.</div>
                )}
            </div>

            <div className="mt-6">
                <Field label="To-Do-Liste für Mieter (Zeilenweise)">
                    <Textarea
                        rows={4}
                        value={(t.pre_inspection?.todo_mieter || []).join("\n")}
                        onChange={(e) => setT({ ...t, pre_inspection: { ...t.pre_inspection, todo_mieter: e.target.value.split("\n").filter(Boolean) } })}
                        placeholder="z.B. Wände weiß streichen"
                        data-testid="vor-todo"
                    />
                </Field>
            </div>

            <div className="flex justify-end pt-5">
                <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-phase2"
                        disabled={gesperrt} grund={grund}>
                    <Save size={14} className="mr-1.5" /> Speichern
                </Button>
            </div>
        </SectionCard>
    );
};

// ---------------- PHASE 3: Übergabe (Daten-Hub) ----------------
const defaultMeters = [
    { art: "Strom", zaehler_nr: "", stand: "" },
    { art: "Gas", zaehler_nr: "", stand: "" },
    { art: "Wasser kalt", zaehler_nr: "", stand: "" },
    { art: "Wasser warm", zaehler_nr: "", stand: "" },
    { art: "Heizung", zaehler_nr: "", stand: "" },
];
const defaultRooms = ["Flur", "Wohnzimmer", "Küche", "Schlafzimmer", "Bad"].map((r) => ({ raum: r, zustand: "ok", bemerkung: "" }));
const defaultKeys = [
    { art: "Wohnungstür", anzahl: 0 },
    { art: "Haustür", anzahl: 0 },
    { art: "Briefkasten", anzahl: 0 },
    { art: "Keller", anzahl: 0 },
];

// Generic Handover form — used for both Auszug & Einzug
const HandoverForm = ({ detail, t, setT, save, fieldName, label, testidPrefix, onTransfer }) => {
    const { gesperrt, grund } = useSchreibschutz();
    const h = t[fieldName] || {};
    const update = (patch) => setT({ ...t, [fieldName]: { ...h, ...patch } });

    const ensureInit = () => {
        const patch = { ...h };
        let dirty = false;
        if (!(h.zählerstände && h.zählerstände.length)) {
            patch.zählerstände = defaultMeters.map((m) => ({ ...m, datum: new Date().toISOString().slice(0, 10) }));
            dirty = true;
        }
        if (!(h.raeume && h.raeume.length)) { patch.raeume = defaultRooms; dirty = true; }
        if (!(h.schluessel && h.schluessel.length)) { patch.schluessel = defaultKeys; dirty = true; }
        if (dirty) setT({ ...t, [fieldName]: patch });
    };
    // Absichtlich nur beim Mount: legt die Standardzeilen (Zähler, Räume,
    // Schlüssel) einmalig an, solange das Protokoll leer ist. Mit `h`/`t` in den
    // Abhängigkeiten würde die Initialisierung bei jeder Eingabe erneut laufen
    // und getippte Werte überschreiben. Darum hier gezielt unterdrückt statt
    // pauschal — das alte `eslint-disable-next-line` ohne Regelnamen hätte auch
    // echte Fehler verdeckt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { ensureInit(); }, []);

    const updateMeter = (i, patch) => {
        const arr = [...(h.zählerstände || [])];
        arr[i] = { ...arr[i], ...patch };
        update({ zählerstände: arr });
    };
    const updateRoom = (i, patch) => {
        const arr = [...(h.raeume || [])];
        arr[i] = { ...arr[i], ...patch };
        update({ raeume: arr });
    };
    const updateKey = (i, patch) => {
        const arr = [...(h.schluessel || [])];
        arr[i] = { ...arr[i], ...patch };
        update({ schluessel: arr });
    };
    const removeKey = (i) => {
        const arr = [...(h.schluessel || [])]; arr.splice(i, 1);
        update({ schluessel: arr });
    };
    const addKey = () => update({ schluessel: [...(h.schluessel || []), { art: "", anzahl: 0, bemerkung: "" }] });
    const addRoom = () => update({ raeume: [...(h.raeume || []), { raum: "Zimmer", zustand: "ok", bemerkung: "" }] });

    const isAuszug = fieldName === "handover_auszug";
    const partyLabel = isAuszug ? "Alter Mieter" : "Neuer Mieter";

    return (
        <div className="space-y-6">
            <SectionCard
                title={`Übergabeprotokoll · ${label}`}
                subtitle={isAuszug
                    ? "Auszug: alter Mieter übergibt an die Hausverwaltung."
                    : "Einzug: Hausverwaltung übergibt an den neuen Mieter (Daten können aus Auszug übernommen werden)."}
                right={
                    <div className="flex items-center gap-2 flex-wrap">
                        {!isAuszug && onTransfer && (
                            <Button variant="outline" size="sm" onClick={onTransfer} data-testid={`${testidPrefix}-transfer`}
                                    disabled={gesperrt} grund={grund}>
                                <ArrowRight size={14} className="mr-1.5" /> Daten aus Auszug übernehmen
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => exportUebergabeprotokoll({ turnover: t, handover: h, variante: label, unit: detail.unit, property: detail.property, entity: detail.entity, oldTenant: detail.old_tenant, newTenant: detail.new_tenant, accessories: detail.accessories })} data-testid={`${testidPrefix}-export`}>
                            <Download size={14} className="mr-1.5" /> Protokoll PDF
                        </Button>
                        {!isAuszug && (
                            <Button variant="outline" size="sm" onClick={() => exportWohnungsgeberbestaetigung({ turnover: t, unit: detail.unit, property: detail.property, entity: detail.entity, newTenant: detail.new_tenant })} data-testid={`${testidPrefix}-wgb`}>
                                <Download size={14} className="mr-1.5" /> Wohnungsgeberbest.
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="Datum">
                        <Input type="date" value={h.datum || ""} onChange={(e) => update({ datum: e.target.value })} data-testid={`${testidPrefix}-datum`} />
                    </Field>
                    <Field label="Uhrzeit">
                        <Input type="time" value={h.uhrzeit || ""} onChange={(e) => update({ uhrzeit: e.target.value })} />
                    </Field>
                    <Field label="Fotos Übergabe (Drive-Link)">
                        <Input value={h.fotos_link || ""} onChange={(e) => update({ fotos_link: e.target.value })} placeholder="https://drive.google.com/..." />
                    </Field>
                    <Field label={`Name ${partyLabel}`}>
                        <Input
                            value={h.neuer_mieter_name || ""}
                            onChange={(e) => update({ neuer_mieter_name: e.target.value })}
                            placeholder={isAuszug ? (detail.old_tenant?.full_name || "") : (detail.new_tenant?.full_name || "")}
                            data-testid={`${testidPrefix}-mieter-name`}
                        />
                    </Field>
                    <Field label={`E-Mail ${partyLabel}`} className="md:col-span-2">
                        <Input value={h.neuer_mieter_email || ""} onChange={(e) => update({ neuer_mieter_email: e.target.value })} />
                    </Field>
                </div>
            </SectionCard>

            <SectionCard title="Zählerstände" subtitle="Mit Fotoreferenz (Drive-Link) je Zähler — sendefertig an die Versorger.">
                <div className="space-y-2">
                    {(h.zählerstände || []).map((m, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end bg-gray-50 border border-gray-200 rounded-md p-3">
                            <Field label="Art" className="col-span-2">
                                <Input value={m.art || ""} onChange={(e) => updateMeter(i, { art: e.target.value })} />
                            </Field>
                            <Field label="Zählernummer" className="col-span-3">
                                <Input value={m.zaehler_nr || ""} onChange={(e) => updateMeter(i, { zaehler_nr: e.target.value })} data-testid={`${testidPrefix}-meter-nr-${i}`} />
                            </Field>
                            <Field label="Stand" className="col-span-2">
                                <Input value={m.stand || ""} onChange={(e) => updateMeter(i, { stand: e.target.value })} data-testid={`${testidPrefix}-meter-stand-${i}`} />
                            </Field>
                            <Field label="Datum" className="col-span-2">
                                <Input type="date" value={m.datum || ""} onChange={(e) => updateMeter(i, { datum: e.target.value })} />
                            </Field>
                            <Field label="Foto-Link" className="col-span-3">
                                <Input value={m.foto_link || ""} onChange={(e) => updateMeter(i, { foto_link: e.target.value })} placeholder="Drive-URL" />
                            </Field>
                        </div>
                    ))}
                </div>
            </SectionCard>

            <SectionCard
                title="Raumzustand & Einbehalte"
                subtitle={isAuszug ? "Mängel-Kosten (Einbehalt) oder Rückstellungen direkt erfassen — wird automatisch in die Kautionsabrechnung übernommen." : "Raumzustand bei Übergabe an den neuen Mieter."}
                right={<Button variant="outline" size="sm" onClick={addRoom} data-testid={`${testidPrefix}-add-room`}
                               disabled={gesperrt} grund={grund}><Plus size={14} className="mr-1" /> Raum / Position</Button>}
            >
                <div className="space-y-2">
                    {(h.raeume || []).map((r, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end bg-gray-50 border border-gray-200 rounded-md p-3">
                            <Field label="Raum / Position" className="col-span-3">
                                <Input value={r.raum} onChange={(e) => updateRoom(i, { raum: e.target.value })} />
                            </Field>
                            <Field label="Zustand" className="col-span-2">
                                <SuchAuswahl wert={r.zustand} onWaehlen={(v) => updateRoom(i, { zustand: v })}
                                    optionen={[
                                        { wert: "ok", text: "In Ordnung" },
                                        { wert: "mangel", text: "Mangel" },
                                        { wert: "sonstiges", text: "Sonstiges" },
                                    ]}
                                />
                            </Field>
                            <Field label="Kommentar" className="col-span-3">
                                <Input placeholder="Bemerkung" value={r.bemerkung || ""} onChange={(e) => updateRoom(i, { bemerkung: e.target.value })} />
                            </Field>
                            {isAuszug && (
                                <>
                                    <Field label="Einbehalt-Typ" className="col-span-2">
                                        <SuchAuswahl wert={r.einbehalt_typ === "auskaution" ? "rueckstellung" : (r.einbehalt_typ || "none")} onWaehlen={(v) => updateRoom(i, { einbehalt_typ: v === "none" ? null : v })}
                                            optionen={[
                                                { wert: "none", text: "— kein —" },
                                                { wert: "einbehalt", text: "Einbehalt" },
                                                { wert: "rueckstellung", text: "Rückstellung" },
                                            ]}
                                        />
                                    </Field>
                                    <Field label="Betrag (€)" className="col-span-2">
                                        <Input type="number" step="0.01" placeholder="0,00" value={r.einbehalt_betrag ?? ""} onChange={(e) => updateRoom(i, { einbehalt_betrag: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                                    </Field>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </SectionCard>

            <SectionCard
                title="Schlüssel & Schließmittel"
                subtitle="Vorausgefüllte Kategorien lassen sich überschreiben oder löschen — eigene Kategorien können ergänzt werden."
                right={<Button variant="outline" size="sm" onClick={addKey} data-testid={`${testidPrefix}-add-key`}
                               disabled={gesperrt} grund={grund}><Plus size={14} className="mr-1" /> Schlüsselart</Button>}
            >
                <div className="space-y-2">
                    {(h.schluessel || []).map((k, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end bg-gray-50 border border-gray-200 rounded-md p-3">
                            <Field label="Art / Kategorie" className="col-span-4">
                                <Input
                                    value={k.art || ""}
                                    onChange={(e) => updateKey(i, { art: e.target.value })}
                                    placeholder="z.B. Wohnungstür, Briefkasten, Tiefgarage, Schließzylinder"
                                    data-testid={`${testidPrefix}-key-art-${i}`}
                                />
                            </Field>
                            <Field label="Anzahl" className="col-span-2">
                                <Input
                                    type="number" min={0}
                                    value={k.anzahl ?? 0}
                                    onChange={(e) => updateKey(i, { anzahl: parseInt(e.target.value || "0") })}
                                    data-testid={`${testidPrefix}-key-anz-${i}`}
                                />
                            </Field>
                            <Field label="Bemerkung" className="col-span-5">
                                <Input value={k.bemerkung || ""} onChange={(e) => updateKey(i, { bemerkung: e.target.value })} placeholder="z.B. Schlüssel-Nr., Sicherungskarte" />
                            </Field>
                            <Button variant="ghost" size="sm" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeKey(i)} data-testid={`${testidPrefix}-key-remove-${i}`}
                                    disabled={gesperrt} grund={grund}>
                                <Trash2 size={14} />
                            </Button>
                        </div>
                    ))}
                    {(!h.schluessel || h.schluessel.length === 0) && (
                        <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md py-6 text-center">Keine Schlüssel erfasst — Vorlage-Kategorien werden beim ersten Öffnen automatisch geladen.</div>
                    )}
                </div>
            </SectionCard>

            <SectionCard title="Einwilligung & Unterschriften" subtitle={isAuszug ? "Unterschriften alter Mieter & Hausverwaltung." : "Unterschriften neuer Mieter & Hausverwaltung."}>
                <div className="space-y-4">
                    <label className="flex items-start gap-3 bg-blue-50/50 border border-blue-100 rounded-md p-3 cursor-pointer">
                        <Checkbox checked={!!h.datenweitergabe_versorger} onCheckedChange={(v) => update({ datenweitergabe_versorger: Boolean(v) })} data-testid={`${testidPrefix}-datenweitergabe`} />
                        <div className="text-sm">
                            <div className="font-medium text-slate-900">Einwilligung Datenweitergabe an Versorger</div>
                            <div className="text-xs text-gray-600 mt-0.5">Mieter willigt ein, dass Zählerstände, Vertrags- und Kontaktdaten zum Zwecke der Ummeldung an die Versorger weitergeleitet werden.</div>
                        </div>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SignaturePad
                            label={`Unterschrift ${partyLabel}`}
                            value={h.unterschrift_mieter}
                            onChange={(v) => update({ unterschrift_mieter: v })}
                            testid={`${testidPrefix}-sig-mieter`}
                        />
                        <SignaturePad
                            label="Unterschrift Hausverwaltung"
                            value={h.unterschrift_vermieter}
                            onChange={(v) => update({ unterschrift_vermieter: v })}
                            testid={`${testidPrefix}-sig-vermieter`}
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-5">
                    <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid={`${testidPrefix}-save`}
                            disabled={gesperrt} grund={grund}>
                        <Save size={14} className="mr-1.5" /> {label}-Übergabe speichern
                    </Button>
                </div>
            </SectionCard>
        </div>
    );
};

const PhaseUebergabe = ({ detail, t, setT, save }) => {
    const [sub, setSub] = useState("auszug");

    const transferFromAuszug = () => {
        const src = t.handover_auszug || {};
        const dst = t.handover_einzug || {};
        setT({
            ...t,
            handover_einzug: {
                ...dst,
                zählerstände: (src.zählerstände || []).map((m) => ({ ...m })),
                raeume: (src.raeume || []).map((r) => ({ ...r })),
                schluessel: (src.schluessel || []).map((k) => ({ ...k })),
                fotos_link: dst.fotos_link || src.fotos_link,
                datenweitergabe_versorger: dst.datenweitergabe_versorger || src.datenweitergabe_versorger,
            },
        });
        toast.success("Daten aus Auszug übernommen — bitte vor Ort prüfen und ggf. anpassen.");
    };

    const auszugDone = Boolean(t.handover_auszug?.datum);
    const einzugDone = Boolean(t.handover_einzug?.datum);

    return (
        <div className="space-y-4">
            <Tabs value={sub} onValueChange={setSub}>
                <TabsList className="bg-gray-100 p-1 rounded-md">
                    <TabsTrigger value="auszug" data-testid="sub-tab-auszug">
                        Auszug
                        {auszugDone && <CheckCircle2 size={12} className="ml-1.5 text-emerald-600" />}
                        <span className="ml-2 text-[10px] text-gray-500">alter Mieter → HV</span>
                    </TabsTrigger>
                    <TabsTrigger value="einzug" data-testid="sub-tab-einzug">
                        Einzug
                        {einzugDone && <CheckCircle2 size={12} className="ml-1.5 text-emerald-600" />}
                        <span className="ml-2 text-[10px] text-gray-500">HV → neuer Mieter</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="auszug" className="mt-4">
                    <HandoverForm
                        detail={detail} t={t} setT={setT} save={save}
                        fieldName="handover_auszug" label="Auszug"
                        testidPrefix="auszug"
                    />
                </TabsContent>

                <TabsContent value="einzug" className="mt-4">
                    <HandoverForm
                        detail={detail} t={t} setT={setT} save={save}
                        fieldName="handover_einzug" label="Einzug"
                        testidPrefix="einzug"
                        onTransfer={auszugDone || (t.handover_auszug?.zählerstände?.length > 0) ? transferFromAuszug : null}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

// ---------------- PHASE 4: Abschluss (Altmieter + Neumieter) ----------------

// --- Sub-Tab: Altmieter (Kautionsabrechnung mit Import aus Phase 3) ----------
const AbschlussAltmieter = ({ detail, t, setT, save }) => {
    const { gesperrt, grund } = useSchreibschutz();
    const d = t.deposit || {};

    // Einbehalte und Rückstellungen aus Phase 3 (Auszug → Raumzustand) separat sammeln.
    // Abhängigkeit ist bewusst t.handover_auszug und nicht ein `|| {}`-Fallback —
    // der erzeugt bei jedem Render ein neues Objekt und macht den useMemo wirkungslos.
    const phase3Split = useMemo(() => {
        const auszug = t.handover_auszug || {};
        const einbehalt = [];
        const rueckstellung = [];
        (auszug.raeume || []).forEach((r) => {
            const betrag = parseFloat(r.einbehalt_betrag) || 0;
            if (!r.einbehalt_typ || betrag <= 0) return;
            const isRueck = r.einbehalt_typ === "rueckstellung" || r.einbehalt_typ === "auskaution";
            const item = {
                position: `${isRueck ? "Rückstellung" : "Einbehalt"} – ${r.raum}${r.bemerkung ? ` (${r.bemerkung})` : ""}`,
                betrag,
                source: "phase3",
            };
            (isRueck ? rueckstellung : einbehalt).push(item);
        });
        return { einbehalt, rueckstellung };
    }, [t.handover_auszug]);

    const importedAbzugSet = useMemo(() => new Set((d.abzüge || []).filter((a) => a.source === "phase3").map((a) => a.position)), [d.abzüge]);
    const importedRueckSet = useMemo(() => new Set((d.rueckstellungen || []).filter((a) => a.source === "phase3").map((a) => a.position)), [d.rueckstellungen]);

    const pendingAbzug = phase3Split.einbehalt.filter((p) => !importedAbzugSet.has(p.position));
    const pendingRueck = phase3Split.rueckstellung.filter((p) => !importedRueckSet.has(p.position));
    const phase3Pending = pendingAbzug.length + pendingRueck.length;

    const importPhase3 = () => {
        if (phase3Pending === 0) { toast.info("Keine neuen Positionen zum Übernehmen."); return; }
        setT({
            ...t,
            deposit: {
                ...d,
                abzüge: [...(d.abzüge || []), ...pendingAbzug],
                rueckstellungen: [...(d.rueckstellungen || []), ...pendingRueck.map((p) => ({ ...p, status: "offen", freigabe_grund: "" }))],
            },
        });
        toast.success(`${phase3Pending} Positionen aus Phase 3 übernommen (${pendingAbzug.length} Einbehalt, ${pendingRueck.length} Rückstellung).`);
    };

    // --- Einbehalte (Abzüge) --------------------------------------------------
    const addAbzug = () => setT({ ...t, deposit: { ...d, abzüge: [...(d.abzüge || []), { position: "", betrag: 0 }] } });
    const updateAbzug = (i, patch) => {
        const arr = [...(d.abzüge || [])];
        arr[i] = { ...arr[i], ...patch };
        setT({ ...t, deposit: { ...d, abzüge: arr } });
    };
    const removeAbzug = (i) => {
        const arr = [...(d.abzüge || [])]; arr.splice(i, 1);
        setT({ ...t, deposit: { ...d, abzüge: arr } });
    };

    // --- Rückstellungen -------------------------------------------------------
    const addRueck = () => setT({ ...t, deposit: { ...d, rueckstellungen: [...(d.rueckstellungen || []), { position: "", betrag: 0, freigabe_grund: "", status: "offen" }] } });
    const updateRueck = (i, patch) => {
        const arr = [...(d.rueckstellungen || [])];
        arr[i] = { ...arr[i], ...patch };
        setT({ ...t, deposit: { ...d, rueckstellungen: arr } });
    };
    const removeRueck = (i) => {
        const arr = [...(d.rueckstellungen || [])]; arr.splice(i, 1);
        setT({ ...t, deposit: { ...d, rueckstellungen: arr } });
    };
    // Rückstellung freigeben = aus Liste entfernen → Betrag fließt in Auszahlung
    const freigebenRueck = (i) => {
        const r = (d.rueckstellungen || [])[i];
        if (!r) return;
        const arr = [...(d.rueckstellungen || [])];
        arr[i] = { ...r, status: "freigegeben" };
        // Wir entfernen freigegebene Rückstellungen nicht, markieren sie nur — so bleibt die Historie.
        setT({ ...t, deposit: { ...d, rueckstellungen: arr } });
        toast.success(`Rückstellung "${r.position}" freigegeben – Betrag wird an Mieter ausgezahlt.`);
    };
    // Rückstellung → in Einbehalt umwandeln
    const umwandelnRueck = (i) => {
        const r = (d.rueckstellungen || [])[i];
        if (!r) return;
        const rArr = [...(d.rueckstellungen || [])];
        rArr[i] = { ...r, status: "umgewandelt" };
        const aArr = [...(d.abzüge || []), { position: r.position, betrag: r.betrag, source: r.source, beleg_link: "" }];
        setT({ ...t, deposit: { ...d, rueckstellungen: rArr, abzüge: aArr } });
        toast.success(`"${r.position}" in Einbehalt umgewandelt.`);
    };

    // --- Berechnungen ---------------------------------------------------------
    const basis = d.basisbetrag ?? detail.old_tenant?.kaution ?? 0;
    const sumAbzüge = (d.abzüge || []).reduce((acc, a) => acc + (parseFloat(a.betrag) || 0), 0);
    // Nur aktive (nicht freigegebene) Rückstellungen reduzieren die Auszahlung
    const aktiveRueck = (d.rueckstellungen || []).filter((r) => r.status !== "freigegeben" && r.status !== "umgewandelt");
    const sumRueck = aktiveRueck.reduce((acc, r) => acc + (parseFloat(r.betrag) || 0), 0);
    const sumNK = parseFloat(d.nachzahlung_nk) || 0;
    const rest = Math.max(basis - sumAbzüge - sumRueck - sumNK, 0);

    useEffect(() => {
        if (d.restbetrag !== rest || d.basisbetrag !== basis) {
            setT({ ...t, deposit: { ...d, basisbetrag: basis, restbetrag: rest } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sumAbzüge, sumRueck, sumNK, basis]);

    return (
        <SectionCard
            title="Kautionsabrechnung Altmieter [M.4.1]"
            subtitle="Kaution, Einbehalte (endgültig), Rückstellungen (temporär) und Nebenkosten-Nachzahlung verrechnen."
            right={
                <div className="flex items-center gap-2 flex-wrap">
                    {phase3Pending > 0 && (
                        <Button variant="outline" size="sm" onClick={importPhase3} data-testid="btn-import-phase3"
                                disabled={gesperrt} grund={grund}>
                            <ArrowRight size={14} className="mr-1.5" /> Aus Phase 3 übernehmen ({phase3Pending})
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => exportKautionsabrechnung({ turnover: t, unit: detail.unit, property: detail.property, entity: detail.entity, oldTenant: detail.old_tenant })} data-testid="btn-export-kaution">
                        <Download size={14} className="mr-1.5" /> Abrechnung PDF
                    </Button>
                </div>
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Basisbetrag Kaution (€)">
                    <Input type="number" step="0.01" value={d.basisbetrag ?? ""} onChange={(e) => setT({ ...t, deposit: { ...d, basisbetrag: parseFloat(e.target.value || "0") } })} data-testid="kaution-basis" />
                </Field>
                <Field label="NK-Nachzahlung (€)">
                    <Input type="number" step="0.01" value={d.nachzahlung_nk ?? ""} onChange={(e) => setT({ ...t, deposit: { ...d, nachzahlung_nk: parseFloat(e.target.value || "0") } })} />
                </Field>
                <Field label="Status">
                    <SuchAuswahl wert={d.status || "offen"} onWaehlen={(v) => setT({ ...t, deposit: { ...d, status: v } })}
                        optionen={[
                            { wert: "offen", text: "Offen" },
                            { wert: "berechnet", text: "Berechnet" },
                            { wert: "ausgezahlt", text: "Ausgezahlt" },
                        ]}
                        testId="kaution-status"
                    />
                </Field>
            </div>

            {/* --- Einbehalte --- */}
            <div className="flex items-center justify-between mt-8 mb-3">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Einbehalte (endgültig)</div>
                    <div className="text-xs text-gray-500 mt-0.5">Belegte, bezifferbare Forderungen – werden dauerhaft von der Kaution abgezogen.</div>
                </div>
                <Button variant="outline" size="sm" onClick={addAbzug} data-testid="btn-add-abzug"
                        disabled={gesperrt} grund={grund}>
                    <Plus size={14} className="mr-1" /> Position
                </Button>
            </div>
            <div className="space-y-2">
                {(d.abzüge || []).map((a, i) => (
                    <div key={i} className={`grid grid-cols-12 gap-2 items-center border rounded-md p-3 ${a.source === "phase3" ? "bg-amber-50/40 border-amber-200" : "bg-gray-50 border-gray-200"}`}>
                        <Input className="col-span-6" placeholder="Bezeichnung" value={a.position} onChange={(e) => updateAbzug(i, { position: e.target.value })} />
                        <Input className="col-span-3" type="number" step="0.01" placeholder="Betrag" value={a.betrag} onChange={(e) => updateAbzug(i, { betrag: parseFloat(e.target.value || "0") })} />
                        <Input className="col-span-2" placeholder="Beleg-Link" value={a.beleg_link || ""} onChange={(e) => updateAbzug(i, { beleg_link: e.target.value })} />
                        <Button variant="ghost" size="sm" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeAbzug(i)} disabled={gesperrt} grund={grund}>
                            <Trash2 size={14} />
                        </Button>
                        {a.source === "phase3" && (
                            <div className="col-span-12 text-[10px] uppercase tracking-wider text-amber-700">aus Phase 3 übernommen</div>
                        )}
                    </div>
                ))}
                {(d.abzüge || []).length === 0 && (
                    <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md py-6 text-center">
                        Keine Einbehalte erfasst.{pendingAbzug.length > 0 && " Tipp: Einbehalte aus Phase 3 lassen sich oben rechts importieren."}
                    </div>
                )}
            </div>

            {/* --- Rückstellungen --- */}
            <div className="flex items-center justify-between mt-8 mb-3">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Rückstellungen (temporär)</div>
                    <div className="text-xs text-gray-500 mt-0.5">Sicherheits­einbehalt bis zu einem Ereignis (z.&nbsp;B. NK-Abrechnung). Kann später freigegeben oder in einen Einbehalt umgewandelt werden.</div>
                </div>
                <Button variant="outline" size="sm" onClick={addRueck} data-testid="btn-add-rueck"
                        disabled={gesperrt} grund={grund}>
                    <Plus size={14} className="mr-1" /> Rückstellung
                </Button>
            </div>
            <div className="space-y-2">
                {(d.rueckstellungen || []).map((r, i) => {
                    const freigegeben = r.status === "freigegeben";
                    const umgewandelt = r.status === "umgewandelt";
                    const inaktiv = freigegeben || umgewandelt;
                    return (
                        <div key={i} className={`border rounded-md p-3 ${inaktiv ? "bg-gray-50 border-gray-200 opacity-60" : r.source === "phase3" ? "bg-sky-50/40 border-sky-200" : "bg-sky-50/20 border-sky-100"}`}>
                            <div className="grid grid-cols-12 gap-2 items-center">
                                <Input className="col-span-5" placeholder="Bezeichnung" value={r.position} onChange={(e) => updateRueck(i, { position: e.target.value })} data-testid={`rueck-pos-${i}`} disabled={inaktiv} grund={sperre([freigegeben, "Rückstellung freigegeben"], [umgewandelt, "Rückstellung in Einbehalt umgewandelt"])} />
                                <Input className="col-span-2" type="number" step="0.01" placeholder="Betrag" value={r.betrag} onChange={(e) => updateRueck(i, { betrag: parseFloat(e.target.value || "0") })} data-testid={`rueck-betrag-${i}`} disabled={inaktiv} grund={sperre([freigegeben, "Rückstellung freigegeben"], [umgewandelt, "Rückstellung in Einbehalt umgewandelt"])} />
                                <Input className="col-span-4" placeholder="Freigabe-Grund (z.B. 31.07.2026 ODER &quot;bis NK-Abrechnung 2026&quot;)" value={r.freigabe_grund || ""} onChange={(e) => updateRueck(i, { freigabe_grund: e.target.value })} data-testid={`rueck-grund-${i}`} disabled={inaktiv} grund={sperre([freigegeben, "Rückstellung freigegeben"], [umgewandelt, "Rückstellung in Einbehalt umgewandelt"])} />
                                <Button variant="ghost" size="sm" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeRueck(i)} disabled={inaktiv || gesperrt} grund={sperre([freigegeben, "Rückstellung freigegeben"], [umgewandelt, "Rückstellung in Einbehalt umgewandelt"], [gesperrt, grund])}>
                                    <Trash2 size={14} />
                                </Button>
                            </div>
                            <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
                                    {r.source === "phase3" && <span className="text-sky-700">aus Phase 3 übernommen</span>}
                                    {freigegeben && <span className="text-emerald-700">● freigegeben → Auszahlung</span>}
                                    {umgewandelt && <span className="text-red-700">● in Einbehalt umgewandelt</span>}
                                    {!inaktiv && <span className="text-sky-700">● offen · reduziert Auszahlung</span>}
                                </div>
                                {!inaktiv && (
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => freigebenRueck(i)} data-testid={`rueck-freigeben-${i}`}
                                                disabled={gesperrt} grund={grund}>
                                            Freigeben (auszahlen)
                                        </Button>
                                        <Button variant="outline" size="sm" className="h-7 text-[11px] border-red-200 text-red-700 hover:bg-red-50" onClick={() => umwandelnRueck(i)} data-testid={`rueck-umwandeln-${i}`}
                                                disabled={gesperrt} grund={grund}>
                                            In Einbehalt umwandeln
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                {(d.rueckstellungen || []).length === 0 && (
                    <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md py-6 text-center">
                        Keine Rückstellungen erfasst.{pendingRueck.length > 0 && " Tipp: Rückstellungen aus Phase 3 lassen sich oben rechts importieren."}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <Field label="IBAN des Mieters (Auszahlung)">
                    <Input value={d.iban || ""} onChange={(e) => setT({ ...t, deposit: { ...d, iban: e.target.value } })} placeholder="DE00 0000 0000 0000 0000 00" />
                </Field>
                <Field label="Auszahlungsdatum">
                    <Input type="date" value={d.auszahlungsdatum || ""} onChange={(e) => setT({ ...t, deposit: { ...d, auszahlungsdatum: e.target.value } })} />
                </Field>
            </div>

            <div className="mt-6 border-t border-gray-200 pt-5">
                <div className="flex items-end justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Auszahlungsbetrag</div>
                        <div className="text-xs text-gray-500 mt-1 tabular-nums">
                            {euro(basis)} − {euro(sumAbzüge)} Einbehalte − {euro(sumRueck)} Rückstellungen − {euro(sumNK)} NK
                        </div>
                    </div>
                    <div className="text-4xl font-heading font-bold text-slate-900 tabular-nums" data-testid="kaution-rest">
                        {euro(rest)}
                    </div>
                </div>
            </div>

            <div className="flex justify-end pt-5">
                <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-altmieter"
                        disabled={gesperrt} grund={grund}>
                    <Save size={14} className="mr-1.5" /> Speichern
                </Button>
            </div>
        </SectionCard>
    );
};

// --- Sub-Tab: Neumieter (Vertragsabschluss) ----------------------------------
const AbschlussNeumieter = ({ detail, t, setT, save }) => {
    const { gesperrt, grund } = useSchreibschutz();
    const c = t.closing_neumieter || {};
    const update = (patch) => setT({ ...t, closing_neumieter: { ...c, ...patch } });

    // IBAN-Vererbung für Mieteingang: Unit > Property > Entity
    const ibanInherited = detail.unit?.iban || detail.property?.iban || detail.entity?.iban || "";

    const raten = c.kaution_raten || [];
    const addRate = () => {
        if (raten.length >= 3) { toast.warning("Maximal 3 Raten möglich."); return; }
        update({ kaution_raten: [...raten, { nr: raten.length + 1, betrag: 0, faellig_am: "", eingegangen: false }] });
    };
    const updateRate = (i, patch) => {
        const arr = [...raten]; arr[i] = { ...arr[i], ...patch };
        update({ kaution_raten: arr });
    };
    const removeRate = (i) => {
        const arr = [...raten]; arr.splice(i, 1);
        // Renumber
        update({ kaution_raten: arr.map((r, idx) => ({ ...r, nr: idx + 1 })) });
    };

    const sumRaten = raten.reduce((acc, r) => acc + (parseFloat(r.betrag) || 0), 0);
    const kautionTotal = parseFloat(c.kaution) || 0;
    const ratenDelta = kautionTotal - sumRaten;

    const monatsmiete = c.mietart === "pauschal"
        ? (parseFloat(c.pauschalmiete) || 0)
        : (parseFloat(c.kaltmiete) || 0) + (parseFloat(c.nebenkosten) || 0) + (parseFloat(c.heizkosten) || 0);

    const prefillFromTenant = () => {
        const nt = detail.new_tenant || {};
        update({
            mietart: nt.pauschalmiete ? "pauschal" : "kalt_nk",
            pauschalmiete: nt.pauschalmiete ?? null,
            kaltmiete: nt.nettokaltmiete ?? null,
            nebenkosten: nt.nebenkosten ?? null,
            kaution: nt.kaution ?? c.kaution ?? null,
        });
        toast.success("Daten aus Neumieter-Stammdaten übernommen.");
    };

    return (
        <SectionCard
            title="Vertragsabschluss Neumieter [M.4.2]"
            subtitle="Mietkonditionen, Kaution (inkl. Raten) und Vertragsbeginn finalisieren."
            right={
                detail.new_tenant ? (
                    <Button variant="outline" size="sm" onClick={prefillFromTenant} data-testid="btn-prefill-neumieter"
                            disabled={gesperrt} grund={grund}>
                        <ArrowRight size={14} className="mr-1.5" /> Aus Neumieter-Stammdaten
                    </Button>
                ) : null
            }
        >
            {/* Vertrag */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Vertragsbeginn">
                    <Input type="date" value={c.vertragsbeginn || ""} onChange={(e) => update({ vertragsbeginn: e.target.value })} data-testid="neu-vertragsbeginn" />
                </Field>
                <Field label="Vertragslaufzeit">
                    <Input value={c.vertragslaufzeit || ""} onChange={(e) => update({ vertragslaufzeit: e.target.value })} placeholder="unbefristet oder TT.MM.JJJJ" />
                </Field>
                <Field label="Status">
                    <div className="flex flex-col gap-2 pt-1">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <Checkbox checked={!!c.vertrag_unterzeichnet} onCheckedChange={(v) => update({ vertrag_unterzeichnet: Boolean(v) })} data-testid="neu-vertrag-unterzeichnet" />
                            Vertrag unterzeichnet
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <Checkbox checked={!!c.kaution_eingegangen} onCheckedChange={(v) => update({ kaution_eingegangen: Boolean(v) })} data-testid="neu-kaution-eingang" />
                            Kaution vollständig eingegangen
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <Checkbox checked={!!c.erste_miete_eingegangen} onCheckedChange={(v) => update({ erste_miete_eingegangen: Boolean(v) })} />
                            Erste Miete eingegangen
                        </label>
                    </div>
                </Field>
            </div>

            {/* Mietkonditionen */}
            <div className="mt-8 mb-3">
                <div className="text-sm font-semibold text-slate-900">Mietkonditionen</div>
                <div className="text-xs text-gray-500 mt-0.5">Pauschalmiete (alles inklusive) oder klassisch Nettokaltmiete + Nebenkosten + ggf. Heizkosten.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Mietart">
                    <SuchAuswahl wert={c.mietart || "kalt_nk"} onWaehlen={(v) => update({ mietart: v })}
                        optionen={[
                            { wert: "kalt_nk", text: "Kalt + Nebenkosten" },
                            { wert: "pauschal", text: "Pauschalmiete" },
                        ]}
                        testId="neu-mietart"
                    />
                </Field>
                {c.mietart === "pauschal" ? (
                    <Field label="Pauschalmiete (€)" className="md:col-span-2">
                        <Input type="number" step="0.01" value={c.pauschalmiete ?? ""} onChange={(e) => update({ pauschalmiete: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="neu-pauschalmiete" />
                    </Field>
                ) : (
                    <>
                        <Field label="Nettokaltmiete (€)">
                            <Input type="number" step="0.01" value={c.kaltmiete ?? ""} onChange={(e) => update({ kaltmiete: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="neu-kaltmiete" />
                        </Field>
                        <Field label="Nebenkosten (€)">
                            <Input type="number" step="0.01" value={c.nebenkosten ?? ""} onChange={(e) => update({ nebenkosten: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="neu-nebenkosten" />
                        </Field>
                        <Field label="Heizkosten (€) – optional" className="md:col-span-3">
                            <Input type="number" step="0.01" value={c.heizkosten ?? ""} onChange={(e) => update({ heizkosten: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                        </Field>
                    </>
                )}
            </div>

            <div className="mt-3 text-xs text-gray-500">
                Kalkulierte Monatsmiete: <span className="font-medium text-slate-700 tabular-nums">{euro(monatsmiete)}</span>
            </div>

            {/* IBAN Mieteingang */}
            <div className="mt-6">
                <Field label="IBAN für Mieteingang">
                    <Input
                        value={c.iban_eingang || ""}
                        onChange={(e) => update({ iban_eingang: e.target.value })}
                        placeholder={ibanInherited ? `${ibanInherited}  (geerbt)` : "DE00 0000 0000 0000 0000 00"}
                        data-testid="neu-iban"
                    />
                    {!c.iban_eingang && ibanInherited && (
                        <div className="text-[11px] text-gray-500 mt-1">
                            Vererbt von {detail.unit?.iban ? "Mieteinheit" : detail.property?.iban ? "Wirtschaftseinheit" : "Mandant"}.
                        </div>
                    )}
                </Field>
            </div>

            {/* Kaution + Raten */}
            <div className="mt-8 mb-3 flex items-end justify-between">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Kaution</div>
                    <div className="text-xs text-gray-500 mt-0.5">Maximal 3 Raten gemäß § 551 BGB.</div>
                </div>
                <Button variant="outline" size="sm" onClick={addRate} disabled={raten.length >= 3 || gesperrt} grund={sperre([raten.length >= 3, "Bereits 3 Raten erfasst"], [gesperrt, grund])} data-testid="btn-add-rate">
                    <Plus size={14} className="mr-1" /> Rate
                </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Kautionshöhe gesamt (€)">
                    <Input type="number" step="0.01" value={c.kaution ?? ""} onChange={(e) => update({ kaution: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="neu-kaution-betrag" />
                </Field>
                <div className="md:col-span-2 flex items-end">
                    <div className="text-xs text-gray-500">
                        Summe Raten: <span className="font-medium text-slate-700 tabular-nums">{euro(sumRaten)}</span>
                        {kautionTotal > 0 && Math.abs(ratenDelta) > 0.01 && (
                            <span className={`ml-2 ${ratenDelta > 0 ? "text-amber-700" : "text-red-600"}`}>
                                ({ratenDelta > 0 ? "noch offen: " : "Überzahlung: "}{euro(Math.abs(ratenDelta))})
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-3 space-y-2">
                {raten.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-gray-50 border border-gray-200 rounded-md p-3">
                        <div className="col-span-1 text-xs text-gray-500">Rate {r.nr}</div>
                        <Input className="col-span-3" type="number" step="0.01" placeholder="Betrag" value={r.betrag} onChange={(e) => updateRate(i, { betrag: parseFloat(e.target.value || "0") })} data-testid={`rate-betrag-${i}`} />
                        <Input className="col-span-3" type="date" value={r.faellig_am || ""} onChange={(e) => updateRate(i, { faellig_am: e.target.value })} />
                        <label className="col-span-4 flex items-center gap-2 text-sm text-slate-700">
                            <Checkbox checked={!!r.eingegangen} onCheckedChange={(v) => updateRate(i, { eingegangen: Boolean(v) })} data-testid={`rate-eingang-${i}`} />
                            eingegangen
                        </label>
                        <Button variant="ghost" size="sm" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeRate(i)} disabled={gesperrt} grund={grund}>
                            <Trash2 size={14} />
                        </Button>
                    </div>
                ))}
                {raten.length === 0 && (
                    <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md py-4 text-center">
                        Keine Raten erfasst – Kaution wird in einer Summe fällig.
                    </div>
                )}
            </div>

            {/* Notizen */}
            <div className="mt-6">
                <Field label="Notizen / Sonderabreden">
                    <Textarea rows={3} value={c.notes || ""} onChange={(e) => update({ notes: e.target.value })} />
                </Field>
            </div>

            <div className="flex justify-end pt-5">
                <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-neumieter"
                        disabled={gesperrt} grund={grund}>
                    <Save size={14} className="mr-1.5" /> Speichern
                </Button>
            </div>
        </SectionCard>
    );
};

const PhaseAbschluss = ({ detail, t, setT, save }) => {
    const [sub, setSub] = useState("altmieter");
    const altDone = t.deposit?.status === "ausgezahlt";
    const neuDone = !!(t.closing_neumieter?.vertrag_unterzeichnet && t.closing_neumieter?.kaution_eingegangen);

    return (
        <div className="space-y-4">
            <Tabs value={sub} onValueChange={setSub}>
                <TabsList className="bg-gray-100 p-1 rounded-md">
                    <TabsTrigger value="altmieter" data-testid="sub-tab-altmieter">
                        Altmieter
                        {altDone && <CheckCircle2 size={12} className="ml-1.5 text-emerald-600" />}
                        <span className="ml-2 text-[10px] text-gray-500">Kautionsabrechnung</span>
                    </TabsTrigger>
                    <TabsTrigger value="neumieter" data-testid="sub-tab-neumieter">
                        Neumieter
                        {neuDone && <CheckCircle2 size={12} className="ml-1.5 text-emerald-600" />}
                        <span className="ml-2 text-[10px] text-gray-500">Vertrag & Kaution</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="altmieter" className="mt-4">
                    <AbschlussAltmieter detail={detail} t={t} setT={setT} save={save} />
                </TabsContent>
                <TabsContent value="neumieter" className="mt-4">
                    <AbschlussNeumieter detail={detail} t={t} setT={setT} save={save} />
                </TabsContent>
            </Tabs>
        </div>
    );
};

// ---------------- Main component ----------------
const TurnoverDetail = () => {
    const { gesperrt, grund } = useSchreibschutz();
    const { id } = useParams();
    const [detail, setDetail] = useState(null);
    const [t, setT] = useState(null);
    const [tab, setTab] = useState("1");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const d = await getTurnover(id);
        setDetail(d);
        setT(d.turnover);
        setTab(String(d.turnover.current_phase || 1));
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!t) return;
        setSaving(true);
        try {
            const updated = await updateTurnover(id, {
                // Konfliktprüfung: ohne die Version würde ein paralleles
                // Speichern die Arbeit des anderen lautlos überschreiben.
                version: t.version,
                termination: t.termination,
                pre_inspection: t.pre_inspection,
                handover_auszug: t.handover_auszug,
                handover_einzug: t.handover_einzug,
                deposit: t.deposit,
                closing_neumieter: t.closing_neumieter,
            });
            setT(updated);
            toast.success("Gespeichert");
        } catch (err) {
            // 409 meldet der Interceptor in api.js mit Erklärung. Alles andere
            // hier, damit ein fehlgeschlagenes Speichern nie stumm bleibt.
            if (err?.response?.status !== 409) {
                toast.error("Nicht gespeichert", {
                    description: err?.response?.data?.detail
                        ? String(err.response.data.detail)
                        : "Die Änderungen sind nicht angekommen. Bitte erneut versuchen.",
                });
            }
        } finally { setSaving(false); }
    };

    const advance = async () => {
        const updated = await advanceTurnover(id);
        setT(updated);
        setTab(String(updated.current_phase));
        toast.success(`Weiter zu Phase ${updated.current_phase}`);
    };

    const hero = useMemo(() => ({
        title: detail?.unit?.unit_label || "Mieterwechsel",
        address: detail?.property?.address || detail?.unit?.address,
        entityName: detail?.entity?.name,
        oldName: detail?.old_tenant?.full_name || "—",
        newName: detail?.new_tenant?.full_name || "—",
    }), [detail]);

    if (!detail || !t) return <div className="p-10 text-gray-400">Lade…</div>;

    return (
        <div className="px-6 md:px-10 py-8 space-y-8 max-w-[1300px] mx-auto">
            <Link to="/wechsel" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-slate-900">
                <ArrowLeft size={14} /> Zurück zu Mieterwechsel
            </Link>

            {/* Hero */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Mieterwechsel</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="turnover-detail-title">{hero.title}</h1>
                    <div className="text-sm text-gray-600 mt-1">{hero.address}</div>
                    {hero.entityName && (
                        <div className="text-xs text-gray-500 mt-1">Eigentümer: <span className="font-medium text-slate-700">{hero.entityName}</span></div>
                    )}
                    {detail.accessories && detail.accessories.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {detail.accessories.map((a) => (
                                <span key={a.id} className="inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded">
                                    Zubehör: {a.unit_label || a.typ}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                        <span><span className="text-gray-400">Auszug:</span> <span className="text-slate-700 font-medium">{hero.oldName}</span></span>
                        <span className="text-gray-300">→</span>
                        <span><span className="text-gray-400">Einzug:</span> <span className="text-slate-700 font-medium">{hero.newName}</span></span>
                        <StatusBadge status={t.status} />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {t.current_phase < 4 && (
                        <Button variant="outline" size="sm" onClick={advance} data-testid="btn-advance"
                                disabled={gesperrt} grund={grund}>
                            Nächste Phase <ArrowRight size={14} className="ml-1.5" />
                        </Button>
                    )}
                    {t.current_phase === 4 && t.deposit?.status === "ausgezahlt" && (
                        <div className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md">
                            <CheckCircle2 size={14} /> Abgeschlossen
                        </div>
                    )}
                </div>
            </div>

            {/* Timeline */}
            <div className="bg-white border border-gray-200 rounded-md p-6">
                <PhaseTimeline currentPhase={parseInt(tab)} onJump={(p) => setTab(String(p))} />
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-gray-100 p-1 rounded-md">
                    <TabsTrigger value="1" data-testid="tab-1">1 · Kündigung</TabsTrigger>
                    <TabsTrigger value="2" data-testid="tab-2">2 · Vorabnahme</TabsTrigger>
                    <TabsTrigger value="3" data-testid="tab-3">3 · Übergabe</TabsTrigger>
                    <TabsTrigger value="4" data-testid="tab-4">4 · Abschluss</TabsTrigger>
                </TabsList>

                <TabsContent value="1" className="mt-6"><PhaseKuendigung detail={detail} t={t} setT={setT} save={save} /></TabsContent>
                <TabsContent value="2" className="mt-6"><PhaseVorabnahme detail={detail} t={t} setT={setT} save={save} /></TabsContent>
                <TabsContent value="3" className="mt-6"><PhaseUebergabe detail={detail} t={t} setT={setT} save={save} /></TabsContent>
                <TabsContent value="4" className="mt-6"><PhaseAbschluss detail={detail} t={t} setT={setT} save={save} /></TabsContent>
            </Tabs>

            {saving && <div className="fixed bottom-6 right-6 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-md">Speichere…</div>}

            <div className="text-[11px] text-gray-400 mt-12">
                Zuletzt aktualisiert: {germanDate(t.updated_at)}
            </div>
        </div>
    );
};

export default TurnoverDetail;
